import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Helmet } from 'react-helmet-async';
import { TrendingUp, Award, Zap } from 'lucide-react';
import { supabase } from '../lib/supabaseClient';
import { ProductCard } from './ProductCard';
import { ErrorBoundary } from './ErrorBoundary';

async function fetchTrendingProducts() {
  const { data, error } = await supabase
    .from('products')
    .select('*')
    .gte('techieday_score', 8.0)
    .order('techieday_score', { ascending: false })
    .order('created_at', { ascending: false })
    .limit(50);

  if (error) throw new Error(error.message);
  return data || [];
}

export function TrendingPage() {
  const { data: products = [], isLoading, error } = useQuery({
    queryKey: ['trendingProducts'],
    queryFn: fetchTrendingProducts,
    refetchInterval: 300000, // 5 minutes
  });

  const title = 'Trending Products - Top Rated Tech 2025';
  const description = 'Discover trending tech products with TechieDay Score above 8/10. Only the best-rated computers, electronics, and tech accessories make it here.';

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-slate-900">
        <div className="max-w-7xl mx-auto px-4 py-8">
          <div className="animate-pulse space-y-6">
            <div className="h-12 bg-gray-200 dark:bg-slate-700 rounded w-1/3"></div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {[...Array(8)].map((_, i) => (
                <div key={i} className="bg-white dark:bg-slate-800 rounded-lg shadow-sm p-6">
                  <div className="h-48 bg-gray-100 dark:bg-slate-700 rounded mb-4"></div>
                  <div className="h-4 bg-gray-200 dark:bg-slate-700 rounded mb-2"></div>
                  <div className="h-6 bg-gray-200 dark:bg-slate-700 rounded w-1/2"></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-slate-900 flex items-center justify-center">
        <div className="p-6 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg max-w-md">
          <p className="text-red-800 dark:text-red-200">Failed to load trending products: {error.message}</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="/trending" />
      </Helmet>

      <div className="min-h-screen bg-gray-50 dark:bg-slate-900">
        <header className="bg-gradient-to-r from-orange-600 to-red-600 text-white">
          <div className="max-w-7xl mx-auto px-4 py-8">
            <div className="text-center">
              <div className="flex items-center justify-center mb-4">
                <TrendingUp className="w-8 h-8 mr-3" />
                <h1 className="text-2xl font-semibold text-white">Trending Products</h1>
              </div>
              <p className="text-xl opacity-90 max-w-3xl mx-auto">
                Top-rated products with TechieDay Score above 8/10
              </p>
              <div className="mt-4 inline-flex items-center text-sm opacity-75">
                <Award className="w-4 h-4 mr-2" />
                Only premium quality products
              </div>
            </div>
          </div>
        </header>

        <main className="max-w-7xl mx-auto px-4 py-8">
          {products.length === 0 ? (
            <div className="text-center py-12">
              <Zap className="w-16 h-16 text-gray-300 dark:text-slate-600 mx-auto mb-4" />
              <h2 className="text-2xl font-semibold text-gray-700 dark:text-slate-300 mb-2">No Trending Products Found</h2>
              <p className="text-gray-500 dark:text-slate-400">Check back later for top-rated products.</p>
            </div>
          ) : (
            <>
              <div className="mb-8">
                <div className="bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-lg p-4">
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <div className="flex items-center">
                      <Award className="w-5 h-5 text-orange-600 dark:text-orange-400 mr-2" />
                      <span className="font-medium text-orange-800 dark:text-orange-200">
                        {products.length} Premium Products Found
                      </span>
                    </div>
                    <div className="text-sm text-orange-600 dark:text-orange-400">
                      🔥 TechieDay Score ≥ 8.0/10
                    </div>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {products.map((product) => (
                  <ErrorBoundary key={product.asin}>
                    <ProductCard product={product} />
                  </ErrorBoundary>
                ))}
              </div>
            </>
          )}
        </main>
      </div>
    </>
  );
}
